import React, { useContext, useEffect, useRef } from "react";
import { AsynchronousNotificationContext } from "../../../AsynchronousNotificationContextProvider";
import { apis } from "../../../backend";
import { getBrowserName } from "../../../utils/browser";
import { isTabLatestFocused } from "../../../utils/tabs";
import { getUserId, isRemovableSupportLogin } from "../../../utils/user";
import { createSystemNotificationPermissionRequest } from "../../core/AsynchronousNotificationList/helpers";
import { IncomingCall } from "../../../models/IncomingCall";
import { createIncomingCallNotification } from "./notifications";
import { CohearingMessageContext } from "../CohearingMessageContextProvider";
import { CohearingMessage } from "../../../models/CohearingMessage";
import { CohearingMessageSectionContext } from "../TelephonyViewportWidget/CohearingMessagesSection/CohearingMessagesSectionContextProvider";
import { text } from "../../../utils/i18n";
import { TelephonyContext } from "../TelephonyContext";
import { CohearingContext } from "../CohearingContextProvider";
import { playCallInboundNotification } from "../utils";
import { globalUserStore } from "../../user/UserDispatcher";
import { AsynchronousNotification } from "../../../models/AsynchronousNotification";
import { countCalls } from "../../calls/service";
import { CallConnectionState, CallFilters } from "../../../models/CallFilters";

export enum MessageDataType {
  Pong = 'pong',
  IncomingCall = 'incomingCall',
  /**
   * For users with multiple tabs, this message is sent to one of any tabs,
   * and used to create at most one system notification for the incoming call.
   */
  IncomingCallOnce = 'incomingCallOnce',
  CallEnd = 'callEnd',
  CallTimeout = 'callTimeout',
  CallHangUp = 'callHangUp',
  CallDecline = 'callDecline',
  CallAnswer = 'callAnswer',

  /**
   * Received by the user in call when the listener user sends a cohearing message.
   */
  CohearingMessageNew = 'cohearingMessageNew',

  /**
   * Received by the listener user in call when the user in call received the message.
   */
  CohearingMessageAcknowledged = 'cohearingMessageAcknowledged',

  CallTakeover = 'callTakeover',
}

export type RealtimeMessage = {
  type: MessageDataType;
  [key: string]: any
};

export type RealtimeMessageListener = (message: RealtimeMessage) => void;

const realtimeListeners: RealtimeMessageListener[] = [];

export function addRealtimeMessageListener(listener: RealtimeMessageListener) {
  realtimeListeners.push(listener);
}

export function removeRealtimeMessageListener(listener: RealtimeMessageListener) {
  const i = realtimeListeners.indexOf(listener);
  if (i !== -1) {
    realtimeListeners.splice(i, 1);
  }
}

/**
 * Connects with a WebSocket to receive updates in realtime.
 * 
 * Automatically notifies when an incoming call is ready to be connected to the user.
 * Incoming call = either transferred inbound or outbound call, or inbound call.
 * 
 * @deprecated Migrate to s2-realtime.
 */
export const TelephonyRealtimeHandler: React.FC = () => {
  const client = useRef<WebSocket>(null);
  const cohearingMessageContext = useContext(CohearingMessageContext);
  const cohearingContext = useContext(CohearingContext);
  const telephonyContext = useContext(TelephonyContext);
  const cohearingMessageSectionContext = useContext(CohearingMessageSectionContext);
  const notificationContext = useContext(AsynchronousNotificationContext);
  const notificationContextRef = useRef(notificationContext);
  const systemNotification = useRef<Notification>(null);
  const clientPingIntervalId = useRef<NodeJS.Timeout>(null);
  const pongTimeoutCloseTimeoutId = useRef<NodeJS.Timeout>(null);
  const retryConnectTimeoutId = useRef<NodeJS.Timeout>(null);
  const subsequentCloseCount = useRef(0);
  const incomingCallAudioIntervalId = useRef(null);
  const incomingCallNotification = useRef<AsynchronousNotification>(null);

  notificationContextRef.current = notificationContext;

  useEffect(() => {
    if (window.Notification?.permission === "default") {
      notificationContext.notify(
        createSystemNotificationPermissionRequest({
          title: text`Missa inte när det ringer`,
          body: text`Tillåt aviseringar i ${getBrowserName()} så du aldrig missar ett samtal.`,
          icon: { name: "phone" }
        })
      );
    }
  }, []);

  useEffect(() => {
    return () => {
      if (retryConnectTimeoutId.current) {
        clearTimeout(retryConnectTimeoutId.current);
        retryConnectTimeoutId.current = null;
      }
    }
  }, []);

  function onMessage(input: string) {
    let data: RealtimeMessage;
    try {
      data = JSON.parse(input);
    } catch (error) {
      console.error("Could not decode WebSocket message data:", input, error);
      return;
    }

    for (const listener of realtimeListeners) {
      try {
        listener(data);
      } catch (error) {
        console.error("Realtime message listener error", error);
      }
    }

    try {
      switch (data.type) {
        case MessageDataType.Pong:
          clearTimeout(pongTimeoutCloseTimeoutId.current);
          delayPongTimeoutClose();
          break;

        case MessageDataType.IncomingCall:
          //  For call transfers, data.incomingCallNotificationId is a unique ID per transfer and allows the same call to be notified multiple times without risking duplicate notifications. For inbound calls, a unique ID per call.
          onIncomingCall(new IncomingCall(data.call), data.incomingCallNotificationId);
          break;

        case MessageDataType.IncomingCallOnce:
          //  Detta message har kommit flera gånger, så använder isTabLatestFocused istället.
          //onIncomingCallOnce(new IncomingCall(data.call));
          break;


        case MessageDataType.CallDecline:
        case MessageDataType.CallAnswer:
        case MessageDataType.CallTimeout:
        case MessageDataType.CallHangUp:
          systemNotification.current?.close();
          notificationContext.delete({ tags: [data.callId] });
          clearInterval(incomingCallAudioIntervalId.current);
          break;

        case MessageDataType.CohearingMessageNew:
          cohearingMessageContext.addMessage(new CohearingMessage(data.message));
          break;

        case MessageDataType.CohearingMessageAcknowledged:
          cohearingMessageSectionContext.updateHistory(data.messageId, { acknowledgedAt: data.acknowledgedAt });
          break;

        case MessageDataType.CallTakeover:
          if (cohearingContext) {
            cohearingContext.onCallTakeover();
          } else {
            console.warn("[RealtimeHandler] cohearing context is null?");
          }
          break;

        default:
          console.warn("Could not handle WebSocket message with data:", data);
          break;
      }
    } catch (error) {
      console.error("Error handling message:", data, error);
    }
  }

  /**
   * Adds a handler for messages.
   */
  function subscribe(client: WebSocket) {
    client.addEventListener('message', ({ data }) => onMessage(data));
  }

  function onIncomingCall(call: IncomingCall, incomingCallNotificationId: string) {
    const notifications = notificationContextRef.current;

    const notification = createIncomingCallNotification(call, incomingCallNotificationId, () => {
      clearInterval(incomingCallAudioIntervalId.current);
    });

    incomingCallNotification.current = notification;

    notifications.notify(notification);

    if (isTabLatestFocused()) {
      //  Ska bara hända en gång per tabb.
      onIncomingCallOnce(call);
    }
  }

  function onIncomingCallOnce(call: IncomingCall) {
    //  Spela ljud och visa notis om man inte redan är i ett samtal.
    countCalls(new CallFilters({
      userIds: [getUserId()],
      connectionState: CallConnectionState.Active,
    })).then(activeCallCount => {
      if (activeCallCount) {
        return;
      }

      if (document.hidden) {
        //  Prevent showing two notifications (an in-app and a system notification) if any tab is not hidden.  
        systemNotification.current?.close();
        systemNotification.current = createIncomingCallSystemNotification(call);
      }

      clearInterval(incomingCallAudioIntervalId.current);

      if (globalUserStore.state.users.current.preferences.callConnectNotificationEnabled) {
        playCallInboundNotification();

        incomingCallAudioIntervalId.current = setInterval(() => {
          const notifications = notificationContextRef.current;

          if (notifications.notifications.find(n => n.id === incomingCallNotification.current.id && !n.isRead)) {
            playCallInboundNotification();

          } else {
            clearInterval(incomingCallAudioIntervalId.current);
          }
        }, 4500);
      }
    });
  }

  function createIncomingCallSystemNotification(call: IncomingCall) {
    if (!window.Notification) {
      //  iOS issues, supposedly.
      return null;
    }

    const systemNotification = new window.Notification(call.isInbound ? call.caller : call.recipient, {
      lang: 'sv-SE',
      body: call.isTransferred ? text`Vidarekopplat samtal` : text`Inkommande samtal`,
      silent: true,
      requireInteraction: true
    });

    systemNotification.addEventListener('click', () => window.focus());

    const visibilityChangeListener = () => {
      if (document.visibilityState === 'visible') {
        systemNotification.close();
        document.removeEventListener('visibilitychange', visibilityChangeListener);
      }
    }

    document.addEventListener('visibilitychange', visibilityChangeListener);
    return systemNotification;
  }

  function createClient() {
    console.log("Creating WS client...");
    stopPing();

    try {
      const protocol = /https/i.test(document.location.protocol) ? 'wss' : 'ws';
      const url = new URL(apis.dial.getUrl('/realtime-v1'), protocol + '://' + document.location.host);
      url.searchParams.set('userId', getUserId());
      url.searchParams.set('location', document.location.pathname);

      client.current = new window.WebSocket(url.href, protocol);
      subscribe(client.current);
    } catch (error) {
      console.error('alert: Failed to create WS client', {
        ...error,
        name: error.name,
        message: error.message,
        stack: error.stack
      });

      setTimeout(() => createClient(), 5000);
      return;
    }

    client.current.addEventListener('close', function (event) {
      clearTimeout(pongTimeoutCloseTimeoutId.current);

      console.warn("WS client closed with code %s: %s", event.code, event.reason);
      if (!subsequentCloseCount.current && event.code !== 1006 && event.code !== 1000) { // Common codes listed here.
        console.error('alert: WS client closed with code: %s: %s', event.code, event.reason ?? 'unknown reason');
      }

      subsequentCloseCount.current++;

      if (subsequentCloseCount.current < 10 && !retryConnectTimeoutId.current) {
        retryConnectTimeoutId.current = setTimeout(() => {
          retryConnectTimeoutId.current = null;
          createClient();
        }, 5000);
      } else {
        console.warn("Max WS subsequent close count reached, not retrying");
      }
    });

    client.current.addEventListener('open', function () {
      console.log("WS client open");
      subsequentCloseCount.current = 0;

      startPing();

      clearTimeout(pongTimeoutCloseTimeoutId.current);
      delayPongTimeoutClose();
    });
  }

  /**
   * Starts periodic ping to keep client connected.
   */
  function startPing() {
    clientPingIntervalId.current = setInterval(function () {
      try {
        client.current.send(JSON.stringify({ type: 'ping' }));
      } catch (error) {
        console.error("Error while sending WS ping", error);
      }
    }, 10e3);
  }

  function delayPongTimeoutClose() {
    pongTimeoutCloseTimeoutId.current = setTimeout(function () {
      console.log("Too long since last pong message from server, restarting connection");
      createClient();
    }, 25e3);
  }

  /**
   * Stops periodic ping to keep client connected.
   */
  function stopPing() {
    clearInterval(clientPingIntervalId.current);
  }

  useEffect(() => {
    if (isRemovableSupportLogin()) {
      console.warn("Not connecting with WS as support user");
      return;
    }

    createClient();

    const unloadListener = () => {
      client.current?.close(1000, "App was closed");
    };

    window.addEventListener('unload', unloadListener);

    return () => {
      window.removeEventListener('unload', unloadListener);
      client.current?.close(1000, "Component was unmounted");
    }
  }, []);

  return null;
}